/** Asistentul, pe ecranul editorului: butonul din dreapta jos și panoul lui.
 *
 *  PANOU PLUTITOR, NU SERTAR CARE ÎMPINGE. Un panou care intră lateral și mută
 *  editorul ar muta PLANUL sub ochii omului exact în clipa în care îl întreabă
 *  ceva despre el. Deci stă peste, în colț, ancorat de butonul din care a ieșit.
 *
 *  CE ȚINE COMPONENTA: discuția și fluxul. Ce se întâmplă cu desenul e al
 *  uneltelor (`unelte.ts`), care sunt chiar pârghiile editorului. Aici se
 *  execută bucla — model → unelte → rezultate → model — și atât.
 *
 *  VOCEA E O CONVERSAȚIE, NU O TASTATURĂ CARE AUDE. Microfonul scrie în ACELAȘI
 *  câmp în care scrie tastatura — se vede ce s-a auzit, cuvânt cu cuvânt —, dar
 *  la capăt nu mai cere un buton: două secunde de tăcere, sau microfonul apăsat
 *  a doua oară, și cererea pleacă. Cine dictează are mâinile pe planșă și
 *  ochii pe desen, iar drumul «vorbește, uită-te, caută săgeata, apasă» rupea
 *  exact asta. Cine vrea să citească întâi ce s-a auzit scrie cu tastatura.
 *
 *  CE VEDE OMUL DIN BUCLĂ: fiecare apel de unealtă, cu numele și cu cifra lui.
 *  Nu ca telemetrie: asistentul schimbă desenul de sub mâna cuiva, iar rândul
 *  «schimba_goluri · 11 atinse» e singura formă în care asta rămâne verificabilă
 *  fără să deschizi documentul. Ce n-a mers se vede roșu, în același loc.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";

import * as chat from "./client";
import { PAGINA_VECHE } from "./cod";
import { feliaPlanului, manifest, unelteEditor } from "./unelte";
import type { ContextEditor, Unealta } from "./unelte";
import { semnaturile } from "./api";
import { deriva } from "../viewer/tronson/doc";
import { bcp47, esteCheie, limba as limbaCurenta, useT, useTn } from "../i18n";
import type { Cheie } from "../i18n";
import { depanareaFirului, rezumatulFirului } from "../viewer/fatada/firAgent";
import type { PasFir, RandFir, StareFir } from "../viewer/fatada/firAgent";
import { impleteste } from "../viewer/plan_agent/rulari";
import Scris from "./scris";
import * as voce from "./voce";
import "./asistent.css";

/** Un apel de unealtă, așa cum se vede pe ecran. */
export type ApelUI = {
  id: string;
  nume: string;
  argumente: Record<string, unknown>;
  stare: "curge" | "ok" | "eroare";
  rezumat?: string;
  /** Ce a întors unealta — se desface la clic. */
  rezultat?: unknown;
  imagine?: string;
};

/** Numele și propozițiile agentului al cărui fir se arată (v. `Props.agent`). */
export type TexteAgent = {
  /** Numele celui care lucrează în capul cardului; `null` = pașii sunt ai
   *  ASISTENTULUI însuși (planul de situație): omul vorbește cu un singur
   *  asistent, cardul spune doar «Am terminat · 12:27», fără nume. */
  titlu: string | null;
  /** starea de la sfârșit, în locul lui «a terminat» / «s-a oprit cu o eroare» */
  terminat?: string;
  cuEroare?: string;
  /** ce face acum («lucrează la fațadă»), în bara firului */
  lucreaza: string;
  /** câmpul de scris e oprit cât lucrează («Agentul de fațadă lucrează…») */
  blocata: string;
  /** titlul butonului care pulsează cu panoul închis */
  buton: string;
};

export type Rand =
  | { fel: "om"; text: string; imagine?: string; despre?: string }
  | { fel: "asistent"; text: string; gand: string; apeluri: ApelUI[] };

/** REZULTATELE UNELTELOR CARE N-AU AJUNS LA SERVER, pe discuție. Uneltele au
 *  rulat deja — agentul de fațadă a pornit, stilul s-a scris —, deci nu se mai
 *  pot pierde: lăsate pe drum, serverul ar refuza următorul mesaj ca venit din
 *  «altă fereastră», iar după `ASTEPTARE_UNELTE_S` ar închide apelurile ca
 *  neexecutate, și modelul ar crede că agentul n-a plecat. Pleacă pe `/unelte`
 *  (o reluare), altfel cu mesajul următor (`rezultate`, v. `asistent/app.py`).
 *  Pe modul, nu în stare: panoul se poate demonta sau trece pe altă soluție cu
 *  ele încă pe drum, iar întors la discuție le trimite tot el. */
const NETRIMISE = new Map<string, Record<string, unknown>[]>();

/** Cât se așteaptă înaintea reluării rezultatelor: o pană de rețea sau un
 *  control plane sufocat (503 la verificarea accesului) trec de obicei în
 *  câteva secunde. */
const RELUARE_MS = 2000;

/** Pana după care merită o reluare: fără răspuns (rețeaua) sau 5xx. Un 4xx e un
 *  refuz, iar reluat ar da același lucru. */
const trecatoare = (err: unknown) =>
  err instanceof chat.EroareAsistent ? err.stare >= 500
    : (err as Error)?.name !== "AbortError";

type Props = {
  projectId: string;
  /** Codul modulului deschis — sau al soluției, pe alt ecran. Discuția e A LUI:
   *  alt modul, altă discuție. */
  cod: string;
  /** Poate omul să schimbe proiectul? Pe citire, asistentul măsoară și explică. */
  potScrie: boolean;
  /** Contextul editorului de module. Lipsește pe alte ecrane (fațada), unde
   *  uneltele își aduc singure contextul, prin închidere. */
  ctx?: ContextEditor;
  /** ECRANUL, așa cum îl știe serviciul (`unde`): `tronson` (implicit) sau
   *  `fatada`. Alege doctrina din prompt și separă discuțiile. */
  unde?: string;
  /** Uneltele ecranului, când nu sunt cele ale editorului de module. */
  unelte?: Unealta[];
  /** Titlul barei, când nu e cel al editorului. */
  titlu?: string;
  /** Rândul de sub titlu: pe ce se vorbește (soluția), în cuvinte. Fără el,
   *  codul modulului. */
  subtitlu?: string;
  /** LOCUL din vizualizator: panoul intră în coloana din dreapta, butonul
   *  lângă comutatorul 2D/3D. Fără el, amândouă plutesc în colțul ferestrei. */
  loc?: { panou: HTMLElement | null; buton: HTMLElement | null };
  /** Anunță ecranul când panoul se deschide sau se închide — ca un panou din
   *  același colț să se strângă și să nu stea peste discuție. */
  laDeschidere?: (deschis: boolean) => void;
  /** FIRUL AGENTULUI DE FAȚADĂ, pe machetă (v. `viewer/fatada/firAgent`): cât
   *  rulează, butonul pulsează — fără niciun text lângă el —, iar în panou,
   *  deasupra discuției, stau propozițiile agentului pentru om și pașii lui. */
  fir?: StareFir | null;
  /** AL CUI E FIRUL: firul de fațadă e cel implicit (`agent.*`); agentul
   *  planului de situație (`viewer/plan_agent`) își dă aici numele și
   *  propozițiile lui, ca bara, butonul și cardul firului să nu spună
   *  «agentul de fațadă» pe o planșă de situație. */
  agent?: TexteAgent;
  /** LIMBAJ OBIȘNUIT (fațada, Cătălin 6.10.2026): omul nu vede nici
   *  raționamentul, nici numele uneltelor, nici JSON-ul, nici costul — numai
   *  răspunsul și, pentru uneltele din `pasi`, un rând spus pe înțeles. */
  prietenos?: boolean;
  /** Rândul fiecărei unelte în modul prietenos: chei de traducere pentru cât
   *  lucrează și pentru după. O unealtă care nu e aici nu se arată deloc. */
  pasi?: Record<string, { curge: string; gata: string }>;
  /** DISCUȚIA OPRITĂ, cu motivul spus în câmp (agentul de fațadă lucrează):
   *  câmpul e gri și nu se trimite nimic. */
  blocat?: string;
  /** BUTONUL ÎNCHIS, cu motivul la hover: pe planul de situație, până îi
   *  desenează agentul circulația (Oana, 2.11.2026) — ce ar cere omul acum
   *  s-ar așeza pe o planșă pe care agentul o rescrie oricum de la zero. */
  dezactivat?: string;
  /** Poza fiecărui mesaj trimis (sau `null`), pentru uneltele care o dau mai departe. */
  laPoza?: (poza: string | null) => void;
  /** STAREA ECRANULUI pe alte ecrane decât editorul de module (fațada: selecția
   *  de pe machetă), trimisă cu fiecare mesaj ca `<stare_ecran>`. */
  stare?: () => unknown;
  /** CIPURILE de deasupra câmpului: ce a ales omul pe machetă și despre ce
   *  vorbește — fiecare cu «×»; `golesteCipuri` le scoate pe toate. */
  cipuri?: { text: string; titlu?: string; scoate: () => void; aprinde?: (da: boolean) => void }[];
  golesteCipuri?: () => void;
  /** BUTONUL DIN CÂMP pe alte ecrane: fațada pune aici «anulează» / «refă»
   *  ultima schimbare (Cătălin, 13.10.2026), iar «discuție nouă» urcă în bara
   *  de sus. Discuția nu se atinge: butonul lucrează pe machetă. */
  actiune?: { fel: "anuleaza" | "reface"; titlu: string; lucreaza: boolean; dezactivat: boolean;
              apasa: () => void };
  /** CARDURI ÎN CONVERSAȚIE, ale ecranului: rulările agentului planului de
   *  situație (`viewer/plan_agent/rulari.ts`). Fiecare stă la sfârșitul
   *  turului celui de-al `dupaOm`-lea mesaj al omului (0 = primul), după
   *  răspunsul asistentului, sau înaintea tuturor (`null`). Cu `carduri` dat, firul (`fir`) NU se mai
   *  arată deasupra discuției: cardul lui viu e în ea. Fără, firul stă sus,
   *  ca pe fațadă. */
  carduri?: { cheie: string; dupaOm: number | null; nod: ReactNode }[];
  /** Câte mesaje ale omului are discuția de pe ecran, de câte ori se schimbă
   *  numărul: ecranul leagă de ultimul ce pornește unealta turului. */
  laMesaje?: (nrOm: number) => void;
  /** Omul a cerut o discuție nouă: ce era legat de mesajele vechi nu mai are
   *  sub ce sta. */
  laDiscutieNoua?: () => void;
};

export default function Asistent({ projectId, cod, potScrie, ctx, unde = "tronson",
                                   unelte: unelteDate, titlu, subtitlu, loc,
                                   laDeschidere, fir, agent: agentDat, prietenos = false, pasi,
                                   blocat, dezactivat, laPoza, stare: stareEcran, cipuri, golesteCipuri, actiune,
                                   carduri, laMesaje, laDiscutieNoua }: Props) {
  const t = useT();
  const tn = useTn();
  // fără altceva spus, firul e al agentului de fațadă
  const agent = useMemo<TexteAgent>(() => agentDat ?? {
    titlu: t("agent.titlu"), lucreaza: t("agent.lucreaza"),
    blocata: t("agent.discutie_blocata"), buton: t("agent.buton_lucreaza"),
  }, [agentDat, t]);
  const [deschis, setDeschis] = useState(false);
  const [conversatie, setConversatie] = useState<string | null>(null);
  const [randuri, setRanduri] = useState<Rand[]>([]);
  const [socoteala, setSocoteala] = useState<chat.Socoteala | null>(null);
  const [lucrez, setLucrez] = useState(false);
  const [eroare, setEroare] = useState<string>("");
  const [necitite, setNecitite] = useState(0);
  /** Firul de calcul nu mai există pe server: singurul leac e o reîncărcare,
   *  iar omul trebuie s-o afle de la ecran, nu dintr-o parafrază a modelului. */
  const [paginaVeche, setPaginaVeche] = useState(false);
  const [scris, setScris] = useState("");
  /** POZA atașată mesajului următor (data URL JPEG, micșorată în browser): o
   *  fotografie de referință pentru fațadă, pe care asistentul o citește ca
   *  imagine și o traduce în reguli. */
  const [poza, setPoza] = useState<string | null>(null);
  const alegePoza = useRef<HTMLInputElement | null>(null);
  const [desfacut, setDesfacut] = useState<Record<string, boolean>>({});
  /** STRÂNS ≠ ÎNCHIS: rămâne bara de sus, cu punctul care pulsează dacă
   *  asistentul lucrează. Închis, panoul dispare cu totul în buton. */
  const [strans, setStrans] = useState(false);
  const [ascult, setAscult] = useState(false);
  // Cititul cu voce tare se alege în profil, pe aparat; aici doar se citește.
  const [cuVoce] = useState(voce.citireaPornita);
  const camp = useRef<HTMLTextAreaElement | null>(null);
  const [toateCipurile, setToateCipurile] = useState(false);
  const [eroareVoce, setEroareVoce] = useState("");

  const opritor = useRef<AbortController | null>(null);
  /** Subiectul de ACUM, citit de turul care curge: un tur pornit pe o soluție
   *  nu mai rulează unelte și nu mai scrie în panou după ce ecranul a trecut
   *  pe alta (v. `curge`). */
  const codRef = useRef(cod);
  codRef.current = cod;
  const jos = useRef<HTMLDivElement | null>(null);
  /** S-a mai căutat o dată discuția de dinainte pentru modulul ăsta?
   *
   *  Fără steagul ăsta, golirea nu se vedea deloc: ea lasă panoul fără
   *  conversație și fără rânduri, adică exact starea în care efectul de mai jos
   *  se duce și reia din bază CEA MAI RECENTĂ discuție — pe care tocmai o
   *  golise omul. Reluarea e o mișcare de DESCHIDERE, o dată per modul; ce face
   *  omul după, inclusiv «discuție nouă», nu se mai răzgândește. */
  const reluat = useRef(false);
  const ascultator = useRef<voce.Ascultator | null>(null);
  /** Ce era scris cu mâna când a pornit microfonul: dictarea se lipește după. */
  const bazaDictarii = useRef("");

  // o selecție nouă se arată iar strânsă
  useEffect(() => { if (!cipuri || cipuri.length <= CIPURI_VAZUTE) setToateCipurile(false); },
            [cipuri]);

  /** Panoul e «văzut» doar desfăcut: strâns, el e doar bara lui de sus. */
  const vazut = deschis && !strans;
  useEffect(() => { laDeschidere?.(vazut); }, [vazut, laDeschidere]);

  // UNELTELE se leagă o dată de context. `ctx` e un obiect de funcții ținut
  // stabil de editor (`useRef`), deci lista nu se reface la fiecare desenare —
  // iar dacă s-ar reface, manifestul trimis la deschidere ar rămâne oricum cel
  // de atunci: modelul trebuie să vadă aceleași unelte tot turul.
  const unelte = useMemo<Unealta[]>(
    () => unelteDate ?? (ctx ? unelteEditor(ctx) : []), [ctx, unelteDate]);
  const peNume = useMemo(
    () => new Map(unelte.map((u) => [u.nume, u])), [unelte]);

  // Discuția se derulează la capăt la fiecare rând nou: altfel răspunsul crește
  // sub marginea de jos a panoului și pare că nu se întâmplă nimic.
  useEffect(() => {
    jos.current?.scrollIntoView({ block: "end" });
  }, [randuri, vazut, fir?.randuri.length, carduri?.length]);

  useEffect(() => {
    if (vazut) setNecitite(0);
  }, [vazut]);

  // numărul mesajelor omului, pentru cardurile ecranului (`carduri`): se
  // anunță și după reluarea discuției de pe server, când nu s-a trimis nimic
  const nrOm = randuri.filter((r) => r.fel === "om").length;
  useEffect(() => { laMesaje?.(nrOm); }, [nrOm, laMesaje]);

  // Microfonul și vocea nu supraviețuiesc plecării de pe ecran: un panou
  // demontat care continuă să asculte n-ar mai avea unde să scrie.
  useEffect(() => () => {
    ascultator.current?.renunta();
    voce.taci();
  }, []);

  // MODULUL (SAU SOLUȚIA) SCHIMBAT = ALTĂ DISCUȚIE. Panoul rămâne deschis, dar
  // conversația pleacă: ce s-a vorbit despre M6 n-are ce căuta peste planul lui
  // M2, iar uneltele ar lucra pe alt document decât cel din care a citit
  // modelul. TURUL ÎN CURS SE OPREȘTE: lăsat să curgă, își rula uneltele pe
  // soluția nouă și își punea răspunsul în panoul ei (Cătălin, 21.10.2026).
  // Serverul îl duce oricum la capăt și îl scrie în discuția veche.
  //
  // STĂ ÎNAINTEA RELUĂRII de mai jos: efectele rulează în ordinea scrierii, iar
  // reluarea citește `reluat` — pus pe «false» aici — ca să caute discuția
  // soluției noi. Scris invers, reluarea sărea și panoul rămânea gol până la
  // închis și redeschis, iar primul mesaj deschidea o discuție nouă.
  useEffect(() => {
    opritor.current?.abort();
    opritor.current = null;
    reluat.current = false;
    setConversatie(null);
    setRanduri([]);
    setSocoteala(null);
    setEroare("");
    setLucrez(false);
  }, [projectId, cod]);

  /** DISCUȚIA DE IERI, la deschiderea panoului.
   *
   *  Conversațiile se scriu în baza asistentului, deci ar fi fost ciudat ca
   *  panoul să înceapă de fiecare dată de la zero: omul a cerut ceva înainte de
   *  pauza de prânz și se întoarce să vadă ce a ieșit. Se reia CEA MAI RECENTĂ
   *  discuție despre modulul ăsta; butonul de golire începe una nouă.
   *
   *  Ce nu se reia: uneltele. Manifestul e al conversației și a plecat o dată,
   *  la deschiderea ei — dacă între timp editorul a căpătat unelte noi, ele
   *  intră abia în discuția următoare. Altfel modelul ar fi văzut, în mijlocul
   *  unui tur, altă listă decât cea pe care a citit-o.
   */
  useEffect(() => {
    if (!deschis || reluat.current) return;
    reluat.current = true;
    // Sursa editorului (230 KB) se citește o dată pe pagină și pleacă cu primul
    // mesaj: cerută abia atunci, ar fi ținut trimisul pe loc cât se descarcă.
    // Aici omul tocmai a deschis panoul și n-a apucat să scrie.
    void semnaturile().catch(() => undefined);
    let viu = true;
    chat.listeaza(projectId, cod)
      .then((r) => {
        const ultima = r.conversatii.find((c) => c.mesaje > 0);
        if (!viu || !ultima) return;
        return chat.citeste(ultima.id).then((fisa) => {
          if (!viu) return;
          setConversatie(fisa.id);
          setRanduri(dinFisa(fisa));
          setSocoteala(fisa.socoteala);
        });
      })
      // Panoul se deschide oricum: o discuție veche pe care n-am putut-o citi
      // nu e un motiv să nu poți scrie una nouă.
      .catch(() => undefined);
    return () => { viu = false; };
  }, [deschis, projectId, cod]);

  const conversatiaMea = useCallback(async (): Promise<string> => {
    if (conversatie) return conversatie;
    const fisa = await chat.deschide({
      project_id: projectId, unde, subiect: cod,
      limba: limbaCurenta(), unelte: manifest(unelte),
    });
    setConversatie(fisa.id);
    setSocoteala(fisa.socoteala);
    return fisa.id;
  }, [conversatie, projectId, cod, unelte, unde]);

  /** Execută uneltele cerute de model și întoarce rezultatele lor.
   *
   *  NU SE OPREȘTE LA PRIMA CARE PICĂ. Fiecare apel primește un răspuns —
   *  reușită sau eroare —, fiindcă modelul are nevoie de toate ca să continue,
   *  iar o unealtă picată e o informație, nu un capăt de drum.
   */
  const executa = useCallback(async (apeluri: chat.Apel[]) => {
    const rezultate: Record<string, unknown>[] = [];
    for (const apel of apeluri) {
      const u = peNume.get(apel.nume);
      let r: { continut?: unknown; imagine?: string; eroare?: string;
               atinse?: number; rezumat?: string };
      if (!u) {
        r = { eroare: `Unealta «${apel.nume}» nu există în editor.` };
      } else {
        try {
          r = await u.ruleaza(apel.argumente, ctx as ContextEditor);
        } catch (err) {
          r = { eroare: String((err as Error)?.message ?? err) };
        }
      }
      if (r.eroare === PAGINA_VECHE) setPaginaVeche(true);
      rezultate.push({ id: apel.id, ...r });
      setRanduri((v) => cuApelulInchis(v, apel.id, r));
    }
    return rezultate;
  }, [peNume, ctx]);

  /** Bucla unui tur: fluxul, apoi uneltele, apoi din nou fluxul. */
  const curge = useCallback(async (cale: string, corp: unknown, id: string) => {
    const controler = new AbortController();
    opritor.current = controler;
    // Subiectul pe care a pornit turul: după ce ecranul a trecut pe altul,
    // fluxul e rupt (mai sus), iar ce a apucat să vină — uneltele cerute,
    // socoteala, vocea — nu se mai execută și nu se mai arată aici.
    const al = codRef.current;
    const totAl = () => codRef.current === al && !controler.signal.aborted;
    setLucrez(true);
    setEroare("");
    try {
      type Pas = { cale: string; corp: unknown };
      let urmatorul: Pas | null = { cale, corp };
      // Ce s-a scris în ULTIMA trecere prin buclă: ăla e răspunsul, restul au
      // fost anunțuri dinaintea uneltelor. Se adună aici, nu se caută pe urmă
      // în rânduri, fiindcă acolo ar trebui ghicit unde s-a terminat turul.
      let raspuns = "";
      let reluare = false;
      while (urmatorul) {
        const pas: Pas = urmatorul;
        const { cale: c, corp: b } = pas;
        urmatorul = null;
        raspuns = "";
        if (!reluare) {
          setRanduri((v) => [...v, { fel: "asistent", text: "", gand: "",
                                     apeluri: [] }]);
        }
        let apeluri: chat.Apel[] = [];
        let primit = false;
        try {
          for await (const ev of chat.tur(c, b, controler.signal)) {
            // ORICE EVENIMENT înseamnă că serverul a scris deja rezultatele aduse
            // (le scrie înaintea turului, v. `asistent/app.py`)
            if (!primit) { primit = true; NETRIMISE.delete(id); }
            if (ev.fel === "text") {
              raspuns += ev.text;
              setRanduri((v) => cuText(v, ev.text));
            } else if (ev.fel === "gand") setRanduri((v) => cuGand(v, ev.text));
            else if (ev.fel === "unealta") {
              setRanduri((v) => cuApel(v, {
                id: ev.apel.id, nume: ev.apel.name,
                argumente: (ev.apel.input ?? {}) as Record<string, unknown>,
                stare: "curge",
              }));
            } else if (ev.fel === "eroare") {
              setEroare(t(ev.cod === "ocupata" ? "asistent.ocupat"
                          : ev.cod === "prea_multi_pasi" ? "asistent.prea_multi_pasi"
                          : "asistent.esec"));
            } else if (ev.fel === "gata") {
              apeluri = ev.apeluri ?? [];
            }
          }
        } catch (err) {
          // REZULTATELE, O DATĂ ÎNCĂ: numai ele, nu un mesaj al omului — reluat,
          // un mesaj deja scris de server s-ar fi scris de două ori. Dacă de
          // fapt ajunseseră, serverul răspunde `nimic_de_executat` (mai jos).
          if (!primit && !reluare && c.endsWith("/unelte") && NETRIMISE.has(id)
              && trecatoare(err) && totAl()) {
            reluare = true;
            await new Promise((r) => setTimeout(r, RELUARE_MS));
            if (!totAl()) return;
            urmatorul = pas;
            continue;
          }
          throw err;
        }
        reluare = false;
        if (apeluri.length && totAl()) {
          const rezultate = await executa(apeluri);
          // ținute până le confirmă serverul — și când turul s-a rupt între
          // timp (altă soluție, «Oprește»): uneltele au rulat oricum
          NETRIMISE.set(id, rezultate);
          if (!totAl()) return;
          urmatorul = { cale: `/conversatii/${id}/unelte`, corp: { rezultate } };
        }
      }
      if (!totAl()) return;
      // Rostitul e la CAPĂT, pe răspunsul întreg: citit pe bucăți, cum vin din
      // flux, s-ar auzi tăiat la mijloc de cuvânt.
      if (cuVoce) voce.citesteTare(raspuns, bcp47(limbaCurenta()));
      // Socoteala se reia de la server la sfârșitul turului: ea e a
      // CONVERSAȚIEI, adunată din rândurile scrise, nu o sumă ținută aici.
      const fisa = await chat.citeste(id);
      if (totAl()) setSocoteala(fisa.socoteala);
    } catch (err) {
      if ((err as Error)?.name !== "AbortError" && totAl()) {
        const netrimise = NETRIMISE.has(id);
        if (netrimise && err instanceof chat.EroareAsistent && err.cod === "nimic_de_executat") {
          // AJUNSESERĂ totuși (s-a pierdut doar răspunsul primei încercări) sau
          // le-a închis altă fereastră: nu mai e nimic de dus, iar discuția de
          // pe server spune ce a urmat
          NETRIMISE.delete(id);
          const fisa = await chat.citeste(id).catch(() => null);
          if (fisa && totAl()) { setRanduri(dinFisa(fisa)); setSocoteala(fisa.socoteala); }
          return;
        }
        // DISCUȚIA DESCHISĂ ȘI ÎN ALTĂ FEREASTRĂ: cealaltă a scris între timp
        // (serverul refuză un mesaj peste unelte neterminate, sau rezultatele
        // noastre peste mesajul ei) — nu e o pană, e un rând de așteptat.
        // Numai când fereastra asta n-are ea însăși rezultate pe drum: atunci
        // le duce mesajul următor, și asta se spune.
        const alta = err instanceof chat.EroareAsistent
          && (err.cod === "unelte_in_asteptare" || err.cod === "nimic_de_executat");
        setEroare(netrimise ? t("asistent.netrimise")
                  : alta ? t("asistent.alta_fereastra")
                  : err instanceof chat.EroareAsistent && err.stare === 401
                    ? t("asistent.iesit") : t("asistent.esec"));
      }
    } finally {
      if (opritor.current === controler) { setLucrez(false); opritor.current = null; }
      if (!vazut && totAl()) setNecitite((n) => n + 1);
    }
  }, [executa, t, vazut, cuVoce]);

  /** Trimite un text anume — ce e în câmp sau ce tocmai s-a dictat.
   *
   *  Dictarea nu poate trimite «ce e în câmp»: ea închide microfonul și trimite
   *  în aceeași clipă, iar `scris` de atunci e cel din desenarea de dinainte de
   *  ultimele cuvinte. Deci textul intră pe ușă, nu se citește din stare.
   *
   *  Cu o poză atașată textul poate lipsi: cererea implicită e stilul din poză.
   */
  const trimiteTextul = useCallback(async (brut: string, pozaAnume?: string) => {
    const pusa = pozaAnume ?? poza;
    const textul = brut.trim() || (pusa ? t("asistent.poza_prompt") : "");
    if (!textul || lucrez || blocat) return;
    const imagine = pusa ?? undefined;
    if (imagine) laPoza?.(imagine);
    voce.taci();
    setScris("");
    setPoza(null);
    // despre ce era mesajul, sub bulă: selecția pleacă cu el și apoi se schimbă
    const despre = cipuri?.length
      ? t("asistent.despre", { ce: cipuri.length <= 2 ? cipuri.map((c) => c.text).join(", ")
                                                    : tn("asistent.n_elemente#n", cipuri.length) })
      : undefined;
    setRanduri((v) => [...v, { fel: "om", text: textul, imagine, despre }]);
    try {
      // PÂRGHIILE PLEACĂ CU PRIMUL MESAJ AL DISCUȚIEI, o dată: de-acolo încolo
      // stau în ea și se citesc din cache. Discuția nouă se cunoaște de aici,
      // dinainte de `conversatiaMea()`, care tocmai o deschide.
      // …și NUMAI în editorul de module, al cărui API e: pe fațadă erau zeci de
      // mii de tokeni fără folos, plus o bulă uriașă la redeschidere
      const intaia = !conversatie && !!ctx;
      const id = await conversatiaMea();
      // rezultatele rămase pe drum de la turul trecut închid apelurile lui
      // înaintea mesajului (v. `NETRIMISE`)
      const netrimise = NETRIMISE.get(id);
      await curge(`/conversatii/${id}/mesaj`,
                  { text: textul, imagine, stare: ctx ? stareaEcranului(ctx) : (stareEcran?.() ?? {}),
                    ...(intaia ? { api: await semnaturile() } : {}),
                    ...(netrimise ? { rezultate: netrimise } : {}) }, id);
    } catch (err) {
      setLucrez(false);
      setEroare(err instanceof chat.EroareAsistent && err.cod === "no_access"
                ? t("asistent.fara_acces") : t("asistent.esec"));
    }
  }, [lucrez, blocat, laPoza, poza, conversatie, conversatiaMea, curge, ctx, stareEcran, cipuri, t, tn]);

  /** Poza aleasă sau lipită, MICȘORATĂ în browser (cel mult 1600 px, JPEG):
   *  o fotografie de telefon are 4 000 px și 3 MB, iar modelul n-are nevoie de
   *  ei ca să citească materiale și ritmuri. */
  const iaPoza = useCallback((f: File | null | undefined) => {
    if (!f || !f.type.startsWith("image/")) return;
    const url = URL.createObjectURL(f);
    const img = new Image();
    img.onload = () => {
      const k = Math.min(1, 1600 / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = Math.max(1, Math.round(img.width * k));
      c.height = Math.max(1, Math.round(img.height * k));
      c.getContext("2d")?.drawImage(img, 0, 0, c.width, c.height);
      try { setPoza(c.toDataURL("image/jpeg", 0.85)); } catch { /* pânză murdară */ }
      URL.revokeObjectURL(url);
    };
    img.src = url;
  }, []);

  const trimite = useCallback(() => {
    // Microfonul se închide FĂRĂ dictat: ce s-a auzit până acum e deja în câmp
    // și pleacă o dată cu el. Lăsat deschis, ar fi lipit vorbele următoare
    // peste un câmp golit, dintr-o bază care nu mai există.
    ascultator.current?.renunta();
    void trimiteTextul(scris);
  }, [trimiteTextul, scris]);

  /** Trimisul, așa cum îl vede microfonul: ultimul, nu cel de la pornirea lui.
   *
   *  Cârligele dictării se leagă o dată, la apăsarea microfonului, și ar fi
   *  rămas cu `trimiteTextul` de atunci — adică fără conversația deschisă între
   *  timp și fără starea ecranului de acum. */
  const trimiteAcum = useRef(trimiteTextul);
  useEffect(() => { trimiteAcum.current = trimiteTextul; }, [trimiteTextul]);

  /** DISCUȚIE NOUĂ = istoricul modulului chiar pleacă.
   *
   *  Golea numai ecranul, iar discuția rămânea cea mai recentă din bază — deci
   *  se întorcea singură la prima redesenare a panoului și butonul părea mort.
   *  Acum se șterg conversațiile modulului pe server (ștergere moale, v. ruta
   *  DELETE), iar `reluat` ține închisă calea prin care s-ar întoarce. Ecranul
   *  se golește PE LOC: omul a cerut o foaie albă, nu o rotiță.
   *
   *  Următorul mesaj deschide o conversație nouă, cu manifestul de unelte de
   *  ACUM — v. `conversatiaMea`.
   */
  const dinNou = useCallback(() => {
    opritor.current?.abort();
    voce.taci();
    reluat.current = true;
    const veche = conversatie;
    if (veche) NETRIMISE.delete(veche);
    setConversatie(null);
    setRanduri([]);
    setSocoteala(null);
    setEroare("");
    setEroareVoce("");
    laDiscutieNoua?.();
    void (async () => {
      try {
        const { conversatii } = await chat.listeaza(projectId, cod);
        const toate = new Set(conversatii.map((c) => c.id));
        if (veche) toate.add(veche);
        // O conversație deja ștearsă de pe altă filă întoarce 404; nu e o
        // ratare — tot ce se cerea era să nu mai fie.
        await Promise.all([...toate].map((id) => chat.sterge(id).catch(
          (err) => { if (!(err instanceof chat.EroareAsistent
                           && err.stare === 404)) throw err; })));
      } catch {
        setEroare(t("asistent.golire_esec"));
      }
    })();
  }, [conversatie, projectId, cod, t, laDiscutieNoua]);

  const inchide = useCallback(() => {
    ascultator.current?.renunta();
    voce.taci();
    setDeschis(false);
  }, []);

  /** Strâns, microfonul se închide FĂRĂ să trimită: câmpul în care scrie nu se
   *  mai vede, iar un panou strâns e o retragere, nu un «gata, asta am de
   *  spus». Ce se auzise rămâne în câmp, pentru când se desface la loc. */
  const comutaStransul = useCallback(() => {
    setStrans((v) => {
      if (!v) ascultator.current?.renunta();
      return !v;
    });
  }, []);

  /** Microfonul. Ce se aude intră în câmpul de scris, după ce e deja scris, și
   *  pleacă singur când omul tace două secunde. A doua apăsare nu mai e «taci»,
   *  ci «gata»: închide și trimite pe loc, fără să aștepte tăcerea. */
  const comutaMicrofonul = useCallback(() => {
    if (ascultator.current) {
      ascultator.current.opreste();
      return;
    }
    // Vocea asistentului tace cât ascultăm: altfel microfonul o aude pe ea.
    voce.taci();
    setEroareVoce("");
    bazaDictarii.current = scris;
    const pornit = voce.asculta({
      limba: bcp47(limbaCurenta()),
      laText: (text) => setScris(voce.lipit(bazaDictarii.current, text)),
      laDictat: (text) => void trimiteAcum.current(
        voce.lipit(bazaDictarii.current, text)),
      laEroare: (cod) => setEroareVoce(
        cod === "not-allowed" || cod === "service-not-allowed"
          ? t("asistent.microfon_refuzat") : t("asistent.microfon_esec", { cod })),
      laSfarsit: () => { ascultator.current = null; setAscult(false); },
    });
    if (!pornit) {
      setEroareVoce(t("asistent.fara_microfon"));
      return;
    }
    ascultator.current = pornit;
    setAscult(true);
  }, [scris, t]);

  /** Un exemplu din panoul gol intră în câmp, nu pleacă: omul îl citește, îl
   *  potrivește pe clădirea lui și abia apoi trimite. Cel cu poza deschide și
   *  alegerea pozei, fiindcă fără ea cererea n-are sens. */
  const alegeExemplul = useCallback((text: string, cuPoza?: boolean) => {
    setScris(text);
    camp.current?.focus();
    if (cuPoza) alegePoza.current?.click();
  }, []);

  /** Rândul de sub titlu spune ce se lucrează ACUM, când panoul nu-l arată:
   *  agentul de fațadă (care blochează discuția) sau asistentul, cu panoul
   *  strâns. Altfel spune pe ce soluție se vorbește. */
  const subtitlul = fir?.ruleaza
    ? <span className="ob-as-sub lucreaza"><span className="ob-as-punct" />{agent.blocata}</span>
    : strans && lucrez
      ? <span className="ob-as-sub lucreaza"><span className="ob-as-punct" />{t("asistent.se_gandeste")}</span>
      : <span className="ob-as-sub">{subtitlu ?? cod}</span>;

  // Cipurile se arată primele trei; o selecție de douăzeci ar fi mâncat
  // jumătate din panou exact deasupra câmpului în care scrii despre ea.
  const cipuriVazute = cipuri && (toateCipurile ? cipuri : cipuri.slice(0, CIPURI_VAZUTE));
  const cipuriAscunse = cipuri ? cipuri.length - (cipuriVazute?.length ?? 0) : 0;

  const panou = deschis && (
    <div className={`ob-as${strans ? " strans" : ""}${loc ? " in-coloana" : " plutitor"}`} role="dialog"
         aria-label={titlu ?? t("asistent.titlu")}>
      <div className="ob-as-sus">
        <div className="ob-as-titluri">
          <b className="ob-as-titlu">{titlu ?? t("asistent.titlu")}</b>
          {subtitlul}
        </div>
        <div className="ob-as-sp" />
        {randuri.length > 0 && !blocat && (
          <button className="btn ob-as-nou" onClick={dinNou}
                  title={t("asistent.conversatie_noua")} aria-label={t("asistent.conversatie_noua")}>
            {t("asistent.nou")}
          </button>
        )}
        <button className="ob-as-icon" onClick={comutaStransul}
                aria-label={t(strans ? "asistent.desfa" : "asistent.strange")}
                title={t(strans ? "asistent.desfa" : "asistent.strange")}>
          <Pictograma fel={strans ? "sus" : "jos"} />
        </button>
        <button className="ob-as-icon" onClick={inchide}
                aria-label={t("asistent.inchide")} title={t("asistent.inchide")}>
          <Pictograma fel="x" />
        </button>
      </div>

      {!strans && (
        <div className="ob-as-corp">
          {/* firul SUS numai fără carduri (fațada): cu ele, rularea vie e un
              card în discuție, la sfârșitul turului cererii ei */}
          {!carduri && fir && (fir.ruleaza || fir.randuri.length > 0) && <FirulAgentului fir={fir} agent={agent} />}
          {!randuri.length && (carduri ? !carduri.length : !(fir && (fir.ruleaza || fir.randuri.length > 0))) && (
            <Gol potScrie={potScrie} unde={unde} alege={alegeExemplul} />
          )}
          {impleteste(randuri, carduri ?? []).map((el) => el.fel === "card"
            ? <div key={`c:${el.card.cheie}`} className="ob-as-card-rulare">{el.card.nod}</div>
            : <RandulLui key={el.i} id={`r${el.i}`} rand={el.rand} desfacut={desfacut}
                         prietenos={prietenos} pasi={pasi}
                         laDesfacut={(id) => setDesfacut(
                           (d) => ({ ...d, [id]: !d[id] }))} />)}
          {lucrez && (
            <div className="ob-as-lucru">
              <span className="ob-as-punct" />
              {t("asistent.se_gandeste")}
              <div className="ob-as-sp" />
              <button className="btn ob-as-mic"
                      onClick={() => opritor.current?.abort()}>
                {t("asistent.opreste")}
              </button>
            </div>
          )}
          {paginaVeche && (
            <div className="ob-as-eroare ob-as-cu-buton" role="alert">
              <Pictograma fel="alerta" marime={15} />
              <div className="ob-as-eroare-corp">
                <span>{t("asistent.pagina_veche")}</span>
                <button className="btn ob-as-mic"
                        onClick={() => window.location.reload()}>
                  {t("asistent.reincarca")}
                </button>
              </div>
            </div>
          )}
          {eroare && (
            <div className="ob-as-eroare" role="alert"><Pictograma fel="alerta" marime={15} /><span>{eroare}</span></div>
          )}
          {eroareVoce && (
            <div className="ob-as-eroare" role="alert"><Pictograma fel="alerta" marime={15} /><span>{eroareVoce}</span></div>
          )}
          <div ref={jos} />
        </div>
      )}

      {!strans && (
        <div className="ob-as-jos">
          {cipuri && cipuri.length > 0 && (
            <div className="ob-as-cipuri" aria-label={t("asistent.selectie")}>
              <span className="ob-as-cipuri-cap">{t("asistent.selectie")}</span>
              {cipuriVazute!.map((c, i) => (
                <span key={i} className="ob-as-cip" title={c.titlu}
                      onMouseEnter={() => c.aprinde?.(true)} onMouseLeave={() => c.aprinde?.(false)}>
                  <span className="ob-as-cip-text">{c.text}</span>
                  <button onClick={c.scoate} aria-label={t("asistent.scoate_din_selectie")}>
                    <Pictograma fel="x" marime={10} />
                  </button>
                </span>
              ))}
              {cipuriAscunse > 0 && (
                <button className="ob-as-cip-mai" onClick={() => setToateCipurile(true)}
                        title={t("asistent.arata_tot")}>+{cipuriAscunse}</button>
              )}
              <div className="ob-as-sp" />
              {golesteCipuri && cipuri.length > 1 && (
                <button className="ob-as-cipuri-goleste" onClick={golesteCipuri}>
                  {t("asistent.goleste_selectia")}
                </button>
              )}
            </div>
          )}
          {/* O SINGURĂ RAMĂ: câmpul și uneltele lui. Patru cutii cu chenar
              una lângă alta se citeau ca patru controale de importanță egală;
              aici câmpul e subiectul, iar poza, anularea, vocea și trimiterea
              sunt unelte ale lui. */}
          <div className={`ob-as-camp-loc${blocat ? " blocat" : ""}`}>
            {blocat && <span className="ob-as-camp-fir" aria-hidden="true"><span /></span>}
            {poza && (
              <div className="ob-as-poza">
                <img src={poza} alt="" />
                <button onClick={() => setPoza(null)} title={t("asistent.scoate_poza")}
                        aria-label={t("asistent.scoate_poza")}>
                  <Pictograma fel="x" marime={11} />
                </button>
              </div>
            )}
            <textarea
              ref={camp} className="ob-as-camp" value={scris} rows={2}
              disabled={!!blocat} aria-disabled={!!blocat}
              aria-label={t(unde === "costuri" ? "asistent.scrie_costuri" : "asistent.scrie")}
              placeholder={blocat ?? t(unde === "costuri" ? "asistent.scrie_costuri" : "asistent.scrie")}
              onChange={(e) => setScris(e.target.value)}
              onPaste={(e) => {
                const f = Array.from(e.clipboardData?.items ?? [])
                  .find((it) => it.type.startsWith("image/"))?.getAsFile();
                if (f) { e.preventDefault(); iaPoza(f); }
              }}
              onKeyDown={(e) => {
                // Enter trimite, ⇧Enter trece pe rândul următor: e ce face
                // orice chat, iar cererile astea au un rând, nu zece.
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  trimite();
                }
              }} />
            <div className="ob-as-unelte">
              {/* POZA: o fotografie de referință (fațadă) — buton sau lipire
                  în câmp; pleacă o dată cu mesajul, ca bloc de imagine. */}
              <input ref={alegePoza} type="file" accept="image/*" style={{ display: "none" }}
                     onChange={(e) => { iaPoza(e.target.files?.[0]); e.target.value = ""; }} />
              {/* pe costuri nu e ce arăta într-o poză */}
              {unde !== "costuri" && (
                <button className="ob-as-icon mic" onClick={() => alegePoza.current?.click()}
                        title={t("asistent.ataseaza_poza")} aria-label={t("asistent.ataseaza_poza")}
                        disabled={lucrez || !!blocat}>
                  <Pictograma fel="poza" />
                </button>
              )}
              {/* ANULEAZĂ / REFĂ pe fațadă: lucrează pe machetă, nu pe discuție,
                  deci stă printre uneltele câmpului, nu în bara de sus. */}
              {actiune && (
                <>
                  <span className="ob-as-despartitor" aria-hidden="true" />
                  <button className={`ob-as-icon mic ob-as-goleste ${actiune.fel}${actiune.lucreaza ? " lucreaza" : ""}`}
                          onClick={actiune.apasa} disabled={actiune.dezactivat}
                          title={actiune.titlu} aria-label={actiune.titlu}>
                    <Pictograma fel={actiune.fel} />
                  </button>
                </>
              )}
              <div className="ob-as-sp" />
              {voce.seAude() && (
                <button className={`ob-as-icon mic ob-as-microfon${ascult ? " ascult" : ""}`}
                        onClick={comutaMicrofonul} aria-pressed={ascult} disabled={!!blocat}
                        title={t(ascult ? "asistent.opreste_dictarea" : "asistent.dicteaza")}
                        aria-label={t(ascult ? "asistent.opreste_dictarea" : "asistent.dicteaza")}>
                  <Pictograma fel="microfon" />
                </button>
              )}
              <button className="ob-as-trimite" disabled={(!scris.trim() && !poza) || lucrez || !!blocat}
                      onClick={trimite} title={t("asistent.trimite")} aria-label={t("asistent.trimite")}>
                <Pictograma fel="sageata" marime={15} />
              </button>
            </div>
          </div>
          {!prietenos && <Socoteala s={socoteala} />}
        </div>
      )}
    </div>
  );

  /** BUTONUL e un cartonaș cu numele, ca vecinul lui 2D/3D, nu un pătrat
   *  colorat: în platformă accentul plin e al selecției, iar un buton plin în
   *  colț era primul lucru care se vedea pe machetă. Deschis, rămâne aprins
   *  (ca un comutator) și tot el închide — fără al doilea «×». Cât lucrează
   *  agentul cu panoul închis, bula are punctul cyan și pe sub nume trece
   *  firul de lucru al platformei, același ca pe tab-urile care se generează. */
  const buton = (
    // `title` și pe cutie: un buton `disabled` nu primește evenimente de mouse
    // în toate browserele, iar tooltip-ul e tot rostul dezactivării
    <div className={`card ob-as-lansator${loc ? "" : " plutitor"}`} title={dezactivat}>
      <button className={`ob-as-buton${deschis ? " deschis" : ""}${!deschis && (fir?.ruleaza || lucrez) ? " pulseaza" : ""}`}
              aria-pressed={deschis} disabled={!!dezactivat}
              onClick={() => { if (deschis) inchide();
                               else { setStrans(false); setDeschis(true); } }}
              title={dezactivat ?? (deschis ? t("asistent.inchide")
                     : fir?.ruleaza ? agent.buton : t("asistent.deschide"))}>
        <span className="ob-as-buton-semn"><Pictograma fel="chat" /></span>
        <span>{t("asistent.titlu")}</span>
        {!deschis && necitite > 0 && (
          <span className="ob-as-semn">{necitite}</span>
        )}
        <span className="ob-as-lansator-linie" aria-hidden="true"><span /></span>
      </button>
    </div>
  );

  // ÎN COLOANA VIZUALIZATORULUI panoul și butonul intră fiecare la locul lui
  // (`loc`), ca să stea în rând cu celelalte panouri; fără `loc` (editorul de
  // module) plutesc, fixe, în colțul ferestrei.
  if (loc) {
    return (
      <>
        {panou && loc.panou && createPortal(panou, loc.panou)}
        {loc.buton && createPortal(buton, loc.buton)}
      </>
    );
  }
  return <>{panou}{buton}</>;
}

// ---------------------------------------------------------------------------
// Bucăți de ecran
// ---------------------------------------------------------------------------

/** Firul agentului de fațadă: ce face, spus omului. Numai de citit — omul nu
 *  vorbește cu agentul de aici; discuția cu asistentul rămâne dedesubt.
 *
 *  CÂT LUCREAZĂ, CRONOLOGIA: fiecare propoziție a agentului cu pasul ei dedesubt,
 *  ultima cu punctul cyan. CÂND A TERMINAT, REZULTATUL: propoziția de încheiere
 *  sus și numerele rulării într-un rând; pașii se desfac la cerere. Douăzeci de
 *  rânduri de aceeași mărime puneau rezultatul la egalitate cu pasul trei. */
export function FirulAgentului({ fir, agent }: { fir: StareFir; agent?: TexteAgent }) {
  const t = useT();
  const tn = useTn();
  const titlu = agent ? agent.titlu : t("agent.titlu");
  const lucreaza = agent?.lucreaza ?? t("agent.lucreaza");
  const [desfacut, setDesfacut] = useState(false);
  // DEPANAREA: doar pentru cine a cerut-o (`depanareaFirului`) și doar când
  // rularea a lăsat rânduri de unelte; comutatorul desface și pașii
  const [depanare, setDepanare] = useState(false);
  const r = rezumatulFirului(fir.randuri, (x) => scurtulPasului(x, t));
  const poateDepana = depanareaFirului() && r.pasi.some((p) => p.unelte.length > 0);
  const comutaDepanarea = poateDepana && (
    <button className={`ob-as-fir-comuta${depanare ? " apasat" : ""}`} aria-pressed={depanare}
            title={t("agent.depanare.titlu")}
            onClick={() => { setDepanare((d) => !d); if (!depanare) setDesfacut(true); }}>
      {"{ }"}
    </button>
  );
  const meta = [
    r.varianta && t("agent.meta.varianta", r.varianta),
    r.referinte > 0 && tn("agent.meta.referinte#n", r.referinte),
    r.unghiuri > 0 && tn("agent.meta.unghiuri#n", r.unghiuri),
    r.observatii > 0 && tn("agent.meta.observatii#n", r.observatii),
  ].filter(Boolean).join(" · ");

  if (fir.ruleaza) {
    return (
      <section className="ob-as-fir lucreaza" aria-live="polite">
        <span className="ob-as-fir-linie" aria-hidden="true"><span /></span>
        <div className="ob-as-fir-cap">
          {titlu && <b>{titlu}</b>}
          <span>{lucreaza}</span>
          <div className="ob-as-sp" />
          {r.varianta && <span>{t("agent.meta.varianta", r.varianta)}</span>}
          {comutaDepanarea}
        </div>
        <Cronologia pasi={r.pasi} ruleaza depanare={depanare} />
      </section>
    );
  }

  const ultimul = r.pasi[r.pasi.length - 1];
  const cuEroare = !r.final && !!ultimul?.eroare;
  const rezultat = r.final?.text ?? (ultimul?.text || ultimul?.etichete[0] || "");
  const ora = r.la ? new Date(r.la).toLocaleTimeString(bcp47(limbaCurenta()),
                                                       { hour: "2-digit", minute: "2-digit" }) : "";
  return (
    <section className={`ob-as-fir${cuEroare ? " eroare" : ""}`}>
      <div className="ob-as-fir-cap">
        <span className="ob-as-fir-stare"><Pictograma fel={cuEroare ? "x" : "bifa"} marime={11} /></span>
        {titlu && <b>{titlu}</b>}
        <span>{cuEroare ? (agent?.cuEroare ?? t("agent.cu_eroare"))
                        : (agent?.terminat ?? t("agent.a_terminat"))}{ora && ` · ${ora}`}</span>
        <div className="ob-as-sp" />
        {comutaDepanarea}
        {r.pasi.length > 0 && (
          <button className="ob-as-fir-comuta" aria-expanded={desfacut}
                  onClick={() => setDesfacut((d) => !d)}>
            {tn("agent.pasi#n", r.pasi.length)}
            <Pictograma fel={desfacut ? "sus" : "jos"} marime={12} />
          </button>
        )}
      </div>
      {rezultat && <p className="ob-as-fir-rezultat">{rezultat}</p>}
      {meta && <span className="ob-as-fir-meta">{meta}</span>}
      {desfacut && <Cronologia pasi={r.pasi} ruleaza={false} depanare={depanare} />}
    </section>
  );
}

/** Pașii agentului ca o cronologie: un semn pe fiecare, legați printr-o linie
 *  subțire, cu ce a făcut sub fiecare propoziție. */
function Cronologia({ pasi, ruleaza, depanare = false }: { pasi: PasFir[]; ruleaza: boolean; depanare?: boolean }) {
  return (
    <ol className="ob-as-cronologie">
      {pasi.map((p, i) => {
        const acum = ruleaza && i === pasi.length - 1;
        return (
          <li key={p.seq} className={`${acum ? "acum" : ""}${p.eroare ? " eroare" : ""}`}>
            <span className="ob-as-cron-semn">
              {acum ? <span className="ob-as-punct" />
                : <Pictograma fel={p.eroare ? "x" : "bifa"} marime={10} />}
            </span>
            <span className="ob-as-cron-corp">
              {p.text
                ? <span className="ob-as-fir-om">{p.text}</span>
                : <span className="ob-as-fir-pas">{p.etichete.join(" · ")}</span>}
              {p.text && p.etichete.length > 0 && (
                <span className="ob-as-fir-pas">{p.etichete.join(" · ")}</span>
              )}
              {depanare && p.unelte.length > 0 && <DetaliiUnelte unelte={p.unelte} />}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/** Textul unei valori de depanare: JSON compact, sau textul gata tăiat de server. */
const jsonDep = (v: unknown): string => (typeof v === "string" ? v : JSON.stringify(v));

/** DETALIILE DE DEPANARE ale unui pas: fiecare unealtă chemată, cu verdictul
 *  (ok / refuzat), ce a primit modelul (eroarea, respingerile, problemele,
 *  schimbările, bilanțul) și, strânse, argumentele cererii. Monospațiat și
 *  mic: e pentru cine depanează, nu pentru cine citește planul. */
function DetaliiUnelte({ unelte }: { unelte: RandFir[] }) {
  const t = useT();
  return (
    <div className="ob-as-dep">
      {unelte.map((u) => {
        const d = u.detaliu ?? {};
        const rez = (d.rezultat && typeof d.rezultat === "object" ? d.rezultat : {}) as Record<string, unknown>;
        const ok = rez.ok !== false && !rez.eroare;
        const liste: [string, unknown][] = (["respingeri", "probleme", "blocaje_refuzate"] as const)
          .filter((k) => Array.isArray(rez[k]) && (rez[k] as unknown[]).length > 0)
          .map((k) => [k, rez[k]]);
        const rest = Object.fromEntries(Object.entries(rez)
          .filter(([k]) => !["ok", "eroare", "respingeri", "probleme", "blocaje_refuzate"].includes(k)));
        return (
          <details key={u.seq} className={ok ? "" : "refuzat"}>
            <summary>
              <b>{u.text}</b>
              <span className="ob-as-dep-verdict">{t(ok ? "agent.depanare.ok" : "agent.depanare.refuzat")}</span>
              {typeof rez.eroare === "string" && <span className="ob-as-dep-scurt">{rez.eroare}</span>}
            </summary>
            {typeof rez.eroare === "string" && <p className="ob-as-dep-eroare">{rez.eroare}</p>}
            {liste.map(([k, v]) => (
              <div key={k}>
                <span className="ob-as-dep-cheie">{k}</span>
                <ul>
                  {(v as Record<string, unknown>[]).map((x, i) => (
                    <li key={i}>{typeof x === "object" && x
                      ? [x.cod ?? x.regula, x.text ?? jsonDep(x)].filter(Boolean).join(": ")
                      : String(x)}</li>
                  ))}
                </ul>
              </div>
            ))}
            {Object.keys(rest).length > 0 && (
              <div><span className="ob-as-dep-cheie">{t("agent.depanare.rezultat")}</span><pre>{jsonDep(rest)}</pre></div>
            )}
            {d.arg !== undefined && (
              <div><span className="ob-as-dep-cheie">{t("agent.depanare.argumente")}</span><pre>{jsonDep(d.arg)}</pre></div>
            )}
          </details>
        );
      })}
    </div>
  );
}

/** Pasul-cod spus scurt (`agent.scurt.*`, `agent_plan.scurt.*`), ca etichetă
 *  sub propoziția lui; un cod pe care ecranul nu-l știe rămâne cu textul lui
 *  întreg. Fiecare agent își ține pașii sub `<agent>.pas.<cod>` și scurtul
 *  lor sub `<agent>.scurt.<cod>`. */
function scurtulPasului(r: RandFir, t: ReturnType<typeof useT>): string {
  const cheie = r.cod && /\.pas\./.test(r.cod) ? r.cod.replace(".pas.", ".scurt.") : null;
  if (cheie && esteCheie(cheie)) return t(cheie, r.param ?? undefined);
  return r.text;
}

function Gol({ potScrie, unde, alege }: {
  potScrie: boolean; unde?: string; alege: (text: string, cuPoza?: boolean) => void;
}) {
  const t = useT();
  const fatada = unde === "fatada";
  const costuri = unde === "costuri";
  // planul de situație: totul trece prin agentul planului (v. `unelteSituatie.ts`)
  const situatie = unde === "situatie";
  const exemple: [Cheie, boolean][] = fatada
    ? [["asistent.exemplu_fatada_culoare", false], ["asistent.exemplu_fatada_material", false],
       ["asistent.exemplu_fatada_poza", true]]
    : costuri
      ? [["asistent.exemplu_costuri_de_unde", false], ["asistent.exemplu_costuri_pret", false],
         ["asistent.exemplu_costuri_structura", false]]
      : situatie
        ? [["asistent.exemplu_situatie_locuri", false], ["asistent.exemplu_situatie_trecere", false],
           ["asistent.exemplu_situatie_verde", false]]
        : [["asistent.exemplu_usi", false], ["asistent.exemplu_pereti", false],
           ["asistent.exemplu_masura", false]];
  return (
    <div className="ob-as-gol">
      <div className="ob-as-gol-cap">
        <h2 className="ob-as-gol-titlu">{t(fatada ? "asistent.gol_titlu_fatada"
          : costuri ? "asistent.gol_titlu_costuri" : situatie ? "asistent.gol_titlu_situatie"
          : "asistent.gol_titlu")}</h2>
        <p className="ob-as-gol-text">{t(fatada ? "asistent.gol_text_fatada"
          : costuri ? "asistent.gol_text_costuri" : situatie ? "asistent.gol_text_situatie"
          : "asistent.gol_text")}</p>
      </div>
      <div className="ob-as-gol-grup">
        <span className="ob-as-eticheta">{t("asistent.incearca")}</span>
        {exemple.map(([k, cuPoza]) => (
          <button key={k} className="ob-as-exemplu" onClick={() => alege(t(k), cuPoza)}>
            <span>{t(k)}</span>
            <Pictograma fel={cuPoza ? "poza" : "sageata"} marime={14} />
          </button>
        ))}
      </div>
      {fatada && (
        <div className="ob-as-gol-grup">
          <span className="ob-as-eticheta">{t("asistent.arata_pe_macheta")}</span>
          <div className="ob-as-taste">
            <kbd>{t("asistent.tasta_clic")}</kbd><span>{t("asistent.un_element")}</span>
            <kbd>{t("asistent.tasta_mai_multe")}</kbd><span>{t("asistent.mai_multe_elemente")}</span>
            <kbd>{t("asistent.tasta_zona")}</kbd><span>{t("asistent.o_zona")}</span>
          </div>
        </div>
      )}
      {!potScrie && (
        <div className="ob-as-nota"><Pictograma fel="lacat" marime={15} /><span>{t("asistent.doar_citire")}</span></div>
      )}
    </div>
  );
}

export function RandulLui({ id, rand, desfacut, laDesfacut, prietenos, pasi }: {
  /** Cheia rândului în harta desfacerilor — raționamentul lui se desface și el. */
  id: string;
  rand: Rand;
  desfacut: Record<string, boolean>;
  laDesfacut: (id: string) => void;
  prietenos?: boolean;
  pasi?: Record<string, { curge: string; gata: string }>;
}) {
  const t = useT();
  if (rand.fel === "om") {
    return (
      <div className="ob-as-om">
        <div className="ob-as-bula">
          {rand.imagine && <img className="ob-as-captura" src={rand.imagine} alt="" />}
          <span>{rand.text}</span>
        </div>
        {rand.despre && <span className="ob-as-despre">{rand.despre}</span>}
      </div>
    );
  }
  if (prietenos) {
    const vazute = rand.apeluri.filter((a) => pasi?.[a.nume]);
    return (
      <div className="ob-as-el">
        {vazute.map((a) => <PasPrietenos key={a.id} apel={a} pas={pasi![a.nume]} />)}
        {rand.text && <div className="ob-as-text"><Scris text={rand.text} /></div>}
        {/* un tur numai de citit (fără pas spus omului, fără text) nu lasă
            nimic în urmă: cât lucrează, rândul «se gândește» de jos e destul */}
      </div>
    );
  }
  return (
    <div className="ob-as-el">
      {rand.gand && (
        <Gand text={rand.gand} curge={!rand.text && !rand.apeluri.length}
              desfacut={!!desfacut[`${id}:gand`]}
              laDesfacut={() => laDesfacut(`${id}:gand`)} />
      )}
      {rand.apeluri.map((a) => (
        a.nume === "plan_de_lucru"
          ? <PlanDeLucru key={a.id} apel={a} />
          : <Apel key={a.id} apel={a} desfacut={!!desfacut[a.id]}
                  laDesfacut={() => laDesfacut(a.id)} />
      ))}
      {rand.text && <div className="ob-as-text"><Scris text={rand.text} /></div>}
      {!rand.text && !rand.apeluri.length && !rand.gand && (
        <div className="ob-as-gand">{t("asistent.se_gandeste")}</div>
      )}
    </div>
  );
}

/** RAȚIONAMENTUL, cu voie să fie citit.
 *
 *  Se vedea un singur rând — cel la care era modelul ACUM —, iar la primul
 *  cuvânt de răspuns pierea cu totul. Bun cât timp lucrează: un panou de chat
 *  nu e un jurnal. Dar când asistentul a mutat un zid și abia pe urmă și-a dat
 *  seama că nu respectă ceva, singurul loc în care scrie DE CE e chiar
 *  raționamentul lui — iar ăla dispăruse. Deci rămâne, strâns la un rând, și se
 *  desface la clic, ca apelurile de unealtă: aceeași mișcare pentru «vreau să
 *  văd dedesubt».
 *
 *  CÂT CURGE, ULTIMUL RÂND. E singurul care spune ceva despre acum; capul de
 *  listă îl înlocuiește după ce răspunsul a venit, fiindcă atunci întrebarea nu
 *  mai e «unde e» ci «ce a gândit». */
function Gand({ text, curge, desfacut, laDesfacut }: {
  text: string; curge: boolean; desfacut: boolean; laDesfacut: () => void;
}) {
  const t = useT();
  return (
    <div className={`ob-as-gand${desfacut ? " desfacut" : ""}`}>
      <button className="ob-as-gand-cap" onClick={laDesfacut}
              aria-expanded={desfacut} title={t("asistent.rationament_arata")}>
        <Pictograma fel={desfacut ? "jos" : "dreapta"} marime={15} />
        <span className="ob-as-gand-rand">
          {desfacut || !curge ? t("asistent.rationament") : ultimulRand(text)}
        </span>
      </button>
      {desfacut && <div className="ob-as-gand-corp">{text}</div>}
    </div>
  );
}

/** Un pas al asistentului spus pe înțeles: fără numele uneltei, fără JSON,
 *  fără nimic de desfăcut. Unde a picat, numai că n-a mers — de ce, spune
 *  asistentul în răspuns. */
function PasPrietenos({ apel, pas }: { apel: ApelUI; pas: { curge: string; gata: string } }) {
  const t = useT();
  // un pas care n-a făcut nimic (plasa a trimis la agent, nimic de anulat) nu
  // se laudă cu «gata»: răspunsul asistentului spune ce urmează
  const r = apel.rezultat as { prea_mult?: boolean; ok?: boolean } | undefined;
  if (apel.stare === "ok" && r && (r.prea_mult || r.ok === false)) return null;
  const text = apel.stare === "curge" ? t(pas.curge as never)
    : apel.stare === "eroare" ? t("asistent.pas.nu_a_mers") : t(pas.gata as never);
  return (
    <div className={`ob-as-pas-prietenos ${apel.stare}`}>
      {apel.stare === "curge" ? <span className="ob-as-rotita"><Pictograma fel="cerc" marime={14} /></span>
        : <Pictograma fel={apel.stare === "eroare" ? "x" : "bifa"} marime={14} />}
      <span>{text}</span>
    </div>
  );
}

function PlanDeLucru({ apel }: { apel: ApelUI }) {
  const t = useT();
  const pasi = (apel.argumente?.pasi ?? []) as { text: string; gata?: boolean }[];
  return (
    <div className="ob-as-plan">
      <div className="ob-as-eticheta">{t("asistent.plan")}</div>
      {pasi.map((p, i) => (
        <div key={i} className={`ob-as-pas${p.gata ? " gata" : ""}`}>
          <span className="ob-as-bifa">{p.gata ? <Pictograma fel="bifa" /> : null}</span>
          <span>{p.text}</span>
        </div>
      ))}
    </div>
  );
}

function Apel({ apel, desfacut, laDesfacut }: {
  apel: ApelUI; desfacut: boolean; laDesfacut: () => void;
}) {
  const t = useT();
  const cifra = apel.stare === "curge" ? t("asistent.se_aplica")
    : apel.rezumat ?? (apel.stare === "eroare" ? t("asistent.esuat")
                                               : t("asistent.gata"));
  return (
    <div className={`ob-as-unealta ${apel.stare}`}>
      <button className="ob-as-unealta-cap" onClick={laDesfacut}>
        <Pictograma fel={apel.stare === "eroare" ? "x"
                         : apel.stare === "curge" ? "cerc" : "bifa"} />
        <span className="ob-as-nume">{apel.nume}</span>
        <div className="ob-as-sp" />
        <span className={`chip ${apel.stare === "eroare" ? "c-bad"
                          : apel.stare === "ok" ? "c-ok" : "c-acc"}`}>{cifra}</span>
      </button>
      {desfacut && (
        <div className="ob-as-unealta-corp">
          <pre>{JSON.stringify(apel.argumente, null, 1)}</pre>
          {apel.rezultat !== undefined && (
            <pre className="ob-as-raspuns">{taiat(apel.rezultat)}</pre>
          )}
        </div>
      )}
      {apel.imagine && <img className="ob-as-captura" src={apel.imagine}
                            alt={t("asistent.captura")} />}
    </div>
  );
}

function Socoteala({ s }: { s: chat.Socoteala | null }) {
  const t = useT();
  if (!s || !s.cereri) return null;
  const k = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1)} k` : String(n));
  return (
    <div className="ob-as-socoteala" title={t("asistent.socoteala_nota")}>
      <span className="ob-as-mono">{s.model.replace("claude-", "")}</span>
      <span>·</span>
      <span className="ob-as-mono">
        {t("asistent.tokeni", { in: k(s.tokeni.in + s.tokeni.cache_citit),
                                out: k(s.tokeni.out) })}
      </span>
      <div className="ob-as-sp" />
      <span className="ob-as-mono">{s.cost_usd.toFixed(2)} $</span>
    </div>
  );
}

/** Pictogramele panoului, pe grila de 20 a platformei (`components/icons`):
 *  trasă de 1,7, 16 px în butoanele de 30–32. Un set al lui, mai mare și mai
 *  gros, făcea panoul să arate lipit de pe alt site; mărimea țintei vine din
 *  buton, nu din desen. */
function Pictograma({ fel, marime = 16 }: { fel: string; marime?: number }) {
  const comun = {
    width: marime, height: marime, viewBox: "0 0 20 20", fill: "none",
    stroke: "currentColor", strokeWidth: 1.7, "aria-hidden": true as const,
    strokeLinecap: "round" as const, strokeLinejoin: "round" as const,
    style: { flex: "none" as const },
  };
  switch (fel) {
    // bula rotunjită: «vorbește cu el», fără colțuri de formular
    case "chat": return (
      <svg {...comun} strokeWidth={1.6}>
        <path d="M10 3.5c3.9 0 7 2.6 7 5.9s-3.1 5.9-7 5.9c-.9 0-1.8-.1-2.6-.4L4 16.5l.9-3.1C3.7 12.3 3 10.9 3 9.4 3 6.1 6.1 3.5 10 3.5z" />
      </svg>);
    case "x": return <svg {...comun}><path d="M5.5 5.5l9 9M14.5 5.5l-9 9" /></svg>;
    case "jos": return <svg {...comun}><path d="M5.5 8l4.5 4.5L14.5 8" /></svg>;
    case "sus": return <svg {...comun}><path d="M5.5 12l4.5-4.5 4.5 4.5" /></svg>;
    case "dreapta": return <svg {...comun}><path d="M8 5.5l4.5 4.5L8 14.5" /></svg>;
    // ANULEAZĂ și REFĂ: aceeași săgeată rotundă, în oglindă — sensul ei spune
    // încotro te duce (înapoi la fațada de dinainte, înainte la cea schimbată)
    case "anuleaza": return (
      <svg {...comun}><path d="M3.5 10a6.5 6.5 0 1 0 1.9-4.6L3.5 7.3" /><path d="M3.5 3.6v3.7h3.7" /></svg>);
    case "reface": return (
      <svg {...comun}><path d="M16.5 10a6.5 6.5 0 1 1-1.9-4.6l1.9 1.9" /><path d="M16.5 3.6v3.7h-3.7" /></svg>);
    case "poza": return (
      <svg {...comun}>
        <rect x="3" y="5" width="14" height="10.5" rx="1.8" />
        <circle cx="10" cy="10.2" r="2.6" />
        <path d="M7.2 5l1-1.7h3.6l1 1.7" />
      </svg>);
    case "microfon": return (
      <svg {...comun}>
        <path d="M10 3a2.4 2.4 0 0 1 2.4 2.4v4.2a2.4 2.4 0 0 1-4.8 0V5.4A2.4 2.4 0 0 1 10 3z" />
        <path d="M5 9.4a5 5 0 0 0 10 0M10 14.4V17M7.5 17h5" />
      </svg>);
    case "bifa": return <svg {...comun}><path d="M4.5 10.5l3.5 3.5 7.5-8" /></svg>;
    case "cerc": return <svg {...comun}><path d="M10 3a7 7 0 1 0 7 7" /></svg>;
    case "alerta": return (
      <svg {...comun}><circle cx="10" cy="10" r="7" /><path d="M10 6.5v4M10 13.4v.1" /></svg>);
    case "lacat": return (
      <svg {...comun}><rect x="4.5" y="9" width="11" height="8" rx="1.5" /><path d="M7 9V6.5a3 3 0 0 1 6 0V9" /></svg>);
    default: return <svg {...comun}><path d="M4 10h12M11 5l5 5-5 5" /></svg>;
  }
}

// ---------------------------------------------------------------------------
// Ajutoare
// ---------------------------------------------------------------------------

/** Câte cipuri de selecție se văd până se cere restul. */
const CIPURI_VAZUTE = 3;

/** Ultimul rând al gândirii: în panou încape o linie, iar linia care contează e
 *  cea la care modelul e ACUM. */
const ultimulRand = (g: string) => g.trimEnd().split("\n").filter(Boolean).pop() ?? "";

const taiat = (v: unknown) => {
  const s = typeof v === "string" ? v : JSON.stringify(v, null, 1);
  return s.length > 4000 ? `${s.slice(0, 4000)}…` : s;
};

/** Ce știe ecranul și modelul n-ar avea de unde: modulul deschis, ce e ales și
 *  planul de acum, în două tabele.
 *
 *  Merge cu fiecare mesaj al omului — «mută peretele ăsta» n-are înțeles fără.
 *
 *  DE CE ȘI PLANUL, NU DOAR SELECȚIA. Primul lucru pe care îl cerea modelul la
 *  orice cerere era `citeste_planul` — sumarul și camerele —, adică un tur întreg
 *  ca să afle ce știe pagina deja. Cele două felii costă vreo patru sute de
 *  tokeni și scot turul ăla din drum. Merg cu FIECARE mesaj, nu o dată: indicii
 *  camerelor se schimbă la fiecare editare, iar o hartă veche ar fi trimis
 *  modelul exact înapoi la citire.
 *
 *  CAMERELE, NU PEREȚII: tabelul camerelor are opt sute de caractere, al
 *  pereților două mii și jumătate, iar cererile pornesc de la o cameră
 *  («lărgește bucătăria»). Pereții se cer când chiar se atinge un zid.
 *
 *  Pe alte ecrane (fațada) nu e niciun editor: uneltele își aduc singure
 *  contextul, iar starea trimisă e goală.
 */
function stareaEcranului(ctx: ContextEditor | undefined) {
  if (!ctx) return {};
  const ales = ctx.ales();
  const doc = ctx.doc();
  const plan = doc ? deriva(doc) : null;
  return {
    modul: ctx.meta(), ales: ales ?? null,
    sumar: doc && plan ? feliaPlanului(plan, doc, "") : null,
    camere: doc && plan ? feliaPlanului(plan, doc, "camere") : null,
  };
}

/** Fișa din bază → rândurile de pe ecran.
 *
 *  Mesajele sunt ținute ca BLOCURI, în forma în care pleacă spre model, fiindcă
 *  aia e forma adevărată a unei discuții cu unelte. Ecranul are nevoie de
 *  altceva: un rând per tur, cu apelurile lui și cu ce au răspuns. Perechea se
 *  face pe `tool_use_id` — `tool_result`-urile vin în mesajul de OM de după.
 */
export function dinFisa(fisa: chat.Fisa): Rand[] {
  const randuri: Rand[] = [];
  for (const m of fisa.mesaje) {
    if (m.rol === "user") {
      for (const b of m.continut) {
        if (b.type === "text") {
          const text = String(b.text ?? "");
          // Starea ecranului și API-ul editorului merg cu mesajul omului, dar
          // n-a scris-o el.
          if (!text.startsWith("<stare_ecran>") && !text.startsWith("<api_editorului>")) {
            randuri.push({ fel: "om", text });
          }
        } else if (b.type === "tool_result") {
          const id = String(b.tool_use_id ?? "");
          for (const r of randuri) {
            if (r.fel !== "asistent") continue;
            const a = r.apeluri.find((x) => x.id === id);
            if (a) {
              a.stare = b.is_error ? "eroare" : "ok";
              a.rezultat = b.content;
            }
          }
        }
      }
      continue;
    }
    const text = m.continut.filter((b) => b.type === "text")
      .map((b) => String(b.text ?? "")).join("");
    const apeluri = m.continut.filter((b) => b.type === "tool_use").map((b) => ({
      id: String(b.id ?? ""), nume: String(b.name ?? "?"),
      argumente: (b.input ?? {}) as Record<string, unknown>,
      // Până se citește `tool_result`-ul de mai jos, un apel scris în bază e
      // unul care s-a și executat: fluxul îl scrie abia după.
      stare: "ok" as const,
    }));
    randuri.push({ fel: "asistent", text, gand: "", apeluri });
  }
  return randuri;
}

const ultimul = (v: Rand[]) => v[v.length - 1];

function cuText(v: Rand[], bucata: string): Rand[] {
  const r = ultimul(v);
  if (!r || r.fel !== "asistent") return v;
  return [...v.slice(0, -1), { ...r, text: r.text + bucata }];
}

function cuGand(v: Rand[], bucata: string): Rand[] {
  const r = ultimul(v);
  if (!r || r.fel !== "asistent") return v;
  return [...v.slice(0, -1), { ...r, gand: r.gand + bucata }];
}

function cuApel(v: Rand[], apel: ApelUI): Rand[] {
  const r = ultimul(v);
  if (!r || r.fel !== "asistent") return v;
  return [...v.slice(0, -1), { ...r, apeluri: [...r.apeluri, apel] }];
}

function cuApelulInchis(v: Rand[], id: string, rezultat: {
  eroare?: string; rezumat?: string; continut?: unknown; imagine?: string;
}): Rand[] {
  return v.map((r) => r.fel !== "asistent" ? r : ({
    ...r,
    apeluri: r.apeluri.map((a) => a.id !== id ? a : ({
      ...a,
      stare: rezultat.eroare ? "eroare" as const : "ok" as const,
      rezumat: rezultat.rezumat ?? rezultat.eroare,
      rezultat: rezultat.eroare ?? rezultat.continut,
      imagine: rezultat.imagine,
    })),
  }));
}
